import { AcneTreatment } from '@data/treatment-types/acne-treatment';
import { BacterialVaginosisTreatment } from '@data/treatment-types/bacterial-vaginosis-treatment';
import { EdTreatmentType } from '@data/treatment-types/ed-treatment-type';
import { FemaleCondomTreatment } from '@data/treatment-types/female-condom-treatment';
import { Glp1RenewalTreatment } from '@data/treatment-types/glp1-renewal-treatment';
import { Glp1Treatment } from '@data/treatment-types/glp1-treatment';
import { HairLossTreatmentType } from '@data/treatment-types/hair-loss-treatment';
import { HemorrhoidsTreatment } from '@data/treatment-types/hemorrhoids-treatment';
import { HsvTreatment } from '@data/treatment-types/hsv-treatment';
import { MaleBvTreatment } from '@data/treatment-types/male-bv-treatment';
import { MetforminTreatment } from '@data/treatment-types/metformin-treatment';
import { NurseTreatment } from '@data/treatment-types/nurse-treatment';
import { PeptideBpc157RenewalTreatmentType } from '@data/treatment-types/peptide-bpc-157-renewal-treatment';
import { PeptideBpc157TreatmentType } from '@data/treatment-types/peptide-bpc-157-treatment';
import { PeptideCjc1295RenewalTreatmentType } from '@data/treatment-types/peptide-cjc-1295-renewal-treatment';
import { PeptideCjc1295TreatmentType } from '@data/treatment-types/peptide-cjc-1295-treatment';
import { PeptideGhkCuRenewalTreatmentType } from '@data/treatment-types/peptide-ghk-cu-renewal-treatment';
import { PeptideGhkCuTreatmentType } from '@data/treatment-types/peptide-ghk-cu-treatment';
import { PeptideGsRenewalTreatmentType } from '@data/treatment-types/peptide-gs-renewal-treatment';
import { PeptideGsTreatmentType } from '@data/treatment-types/peptide-gs-treatment';
import { PeptidePt141RenewalTreatmentType } from '@data/treatment-types/peptide-pt-141-renewal-treatment';
import { PeptidePt141TreatmentType } from '@data/treatment-types/peptide-pt-141-treatment';
import { PeptideRenewalTreatmentType } from '@data/treatment-types/peptide-renewal-treatment';
import { PeptideTb500RenewalTreatmentType } from '@data/treatment-types/peptide-tb-500-renewal-treatment';
import { PeptideTb500TreatmentType } from '@data/treatment-types/peptide-tb-500-treatment';
import { PeptideTreatmentType } from '@data/treatment-types/peptide-treatment';
import { PeptideWsRenewalTreatmentType } from '@data/treatment-types/peptide-ws-renewal-treatment';
import { PeptideWsTreatmentType } from '@data/treatment-types/peptide-ws-treatment';
import { RashTreatment } from '@data/treatment-types/rash-treatment';
import { StallionMetforminTreatment } from '@data/treatment-types/stallion-metformin-treatment';
import { StarfishStdPreventionTreatment } from '@data/treatment-types/starfish-std-prevention-treatment';
import { StarfishStdTreatment } from '@data/treatment-types/starfish-std-treatment';
import { StdPreventionTreatment } from '@data/treatment-types/std-prevention-treatment';
import { StdTreatment } from '@data/treatment-types/std-treatment';
import { TreatMyUtiTreatment } from '@data/treatment-types/treat-my-uti-treatment';
import { TrtRenewalTreatment } from '@data/treatment-types/trt-renewal-treatment';
import { TrtTreatmentType } from '@data/treatment-types/trt-treatment-type';
import { UtiTreatment } from '@data/treatment-types/uti-treatment';
import { YeastInfectionTreatment } from '@data/treatment-types/yeast-infection-treatment';
import { ZofranTreatment } from '@data/treatment-types/zofran-treatment';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { Sites } from '@enums/sites';
import { TreatmentType } from '@models/treatment-types/treatment-type.model';

interface treatmentTypeInfo {
  /**
   * This holds the value when Site is not specified
   */
  default: TreatmentType;
  /**
   * This is an optional Map for site-specific overrides
   */
  siteData?: Map<Sites, TreatmentType>;
}

/**
 * Maps the treatment type enum keys to its TreatmentType instance.
 */
export const treatmentTypeMap = new Map<ConsultationTreatmentTypes, treatmentTypeInfo>([
  [
    ConsultationTreatmentTypes.Uti,
    {
      default: UtiTreatment,
      siteData: new Map<Sites, TreatmentType>([[Sites.Treatmyuti, TreatMyUtiTreatment]]),
    },
  ],
  [ConsultationTreatmentTypes.YeastInfection, { default: YeastInfectionTreatment }],
  [ConsultationTreatmentTypes.BacterialVaginosis, { default: BacterialVaginosisTreatment }],
  [
    ConsultationTreatmentTypes.StdPrevention,
    {
      default: StdPreventionTreatment,
      siteData: new Map<Sites, TreatmentType>([[Sites.Starfish, StarfishStdPreventionTreatment]]),
    },
  ],
  [ConsultationTreatmentTypes.StarfishStd, { default: StarfishStdTreatment }],
  [ConsultationTreatmentTypes.Hsv, { default: HsvTreatment }],
  [
    ConsultationTreatmentTypes.Metformin,
    {
      default: MetforminTreatment,
      siteData: new Map<Sites, TreatmentType>([[Sites.Stallion, StallionMetforminTreatment]]),
    },
  ],
  [ConsultationTreatmentTypes.Glp1, { default: Glp1Treatment }],
  [ConsultationTreatmentTypes.Glp1Renewal, { default: Glp1RenewalTreatment }],
  [ConsultationTreatmentTypes.Ed, { default: EdTreatmentType }],
  [ConsultationTreatmentTypes.Trt, { default: TrtTreatmentType }],
  [ConsultationTreatmentTypes.Peptide, { default: PeptideTreatmentType }],
  [ConsultationTreatmentTypes.PeptideRenewal, { default: PeptideRenewalTreatmentType }],
  [ConsultationTreatmentTypes.TrtRenewal, { default: TrtRenewalTreatment }],
  [ConsultationTreatmentTypes.HairLoss, { default: HairLossTreatmentType }],
  [ConsultationTreatmentTypes.MaleBv, { default: MaleBvTreatment }],
  [ConsultationTreatmentTypes.Zofran, { default: ZofranTreatment }],
  [ConsultationTreatmentTypes.Std, { default: StdTreatment }],
  [ConsultationTreatmentTypes.Nurse, { default: NurseTreatment }],
  [ConsultationTreatmentTypes.Hemorrhoids, { default: HemorrhoidsTreatment }],
  [ConsultationTreatmentTypes.Acne, { default: AcneTreatment }],
  [ConsultationTreatmentTypes.Rash, { default: RashTreatment }],
  [ConsultationTreatmentTypes.FemaleCondom, { default: FemaleCondomTreatment }],
  [ConsultationTreatmentTypes.PeptideWs, { default: PeptideWsTreatmentType }],
  [ConsultationTreatmentTypes.PeptideWsRenewal, { default: PeptideWsRenewalTreatmentType }],
  [ConsultationTreatmentTypes.PeptideGs, { default: PeptideGsTreatmentType }],
  [ConsultationTreatmentTypes.PeptideGsRenewal, { default: PeptideGsRenewalTreatmentType }],
  [ConsultationTreatmentTypes.PeptideBpc157, { default: PeptideBpc157TreatmentType }],
  [ConsultationTreatmentTypes.PeptideBpc157Renewal, { default: PeptideBpc157RenewalTreatmentType }],
  [ConsultationTreatmentTypes.PeptideGhkCu, { default: PeptideGhkCuTreatmentType }],
  [ConsultationTreatmentTypes.PeptideGhkCuRenewal, { default: PeptideGhkCuRenewalTreatmentType }],
  [ConsultationTreatmentTypes.PeptideTb500, { default: PeptideTb500TreatmentType }],
  [ConsultationTreatmentTypes.PeptideTb500Renewal, { default: PeptideTb500RenewalTreatmentType }],
  [ConsultationTreatmentTypes.PeptideCjc1295, { default: PeptideCjc1295TreatmentType }],
  [ConsultationTreatmentTypes.PeptideCjc1295Renewal, { default: PeptideCjc1295RenewalTreatmentType }],
  [ConsultationTreatmentTypes.PeptidePt141, { default: PeptidePt141TreatmentType }],
  [ConsultationTreatmentTypes.PeptidePt141Renewal, { default: PeptidePt141RenewalTreatmentType }],
]);
